import HBIcon from "@/components/HBIcon";
import JsonLd from "@/components/JsonLd";
import useAppStore from "@/context/store";
import TopicBanner from "@/pageComponents/TopicBanner";
import TopicFilter from "@/pageComponents/TopicFilter";
import { TopicLimitPinList } from "@/pageComponents/TopicPinList";
import TopicSimilarList from "@/pageComponents/TopicSimilarList";
import logger from "@/server/logger";
import {
  getDeliveryDetail,
  getTopicDetail,
  getTopicPins,
  searchTopic,
} from "@/services";
import imageUrl from "@/utils/imageUrl";
import { topicDetailFilter } from "@/utils/server/dataFilter";
import {
  serverSideNotFound,
  serverSideRedirect,
} from "@/utils/server/serverSide";
import splitTopicUrlnameAndFilterId from "@/utils/splitTopicUrlnameAndFilterId";
import useReq from "@/utils/useReq";
import { useLocalStorageState } from "ahooks";
import { Statistic } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { isEqual, isNaN, isString } from "lodash";
import Head from "next/head";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { When } from "react-if";
import styles from "./index.module.less";

const { Countdown } = Statistic;

export interface IFilterTopicData {
  title: string;
  description: string;
  img: string;
  bgColor: string;
  labels: {
    text: string;
  }[];
  following: boolean;
  liked: boolean;
  contents: {
    filter: {
      name: string;
      label: string;
      value: number;
    };
    name?: string;
    color: string;
    title: string;
    img: {
      bucket: string;
      key: string;
      type: string;
      width: number;
      height: number;
      frames: number;
    };
  }[];
  filters: {
    icon: string;
    name: string;
    key: string;
    type: "img" | "text";
    showOutside: boolean;
    value: number;
    children: {
      icon?: string;
      label: string;
      value: number;
    }[];
  }[];
}

const filterData = (data): IFilterTopicData => {
  return {
    title: data?.banner?.materials[0]?.title || "",
    description: data?.banner?.materials[0]?.material.description || "",
    img: data?.banner?.materials[0]?.material?.img || "",
    bgColor: data?.banner?.materials[0]?.material?.bgColor || "",
    labels: data?.banner?.materials[0]?.material?.labels || [],
    following: !!data?.following,
    liked: !!data?.liked,
    contents:
      data?.content?.materials?.map((material) => ({
        ...(material?.material || null),
        img: material?.preview?.pins?.[0]?.file || null,
        created_at: material?.created_at || "",
        updated_at: material?.updated_at || "",
      })) || [],
    filters:
      data?.filter?.children?.reverse().map((child) => ({
        icon: child?.preview?.icon?.url || "",
        name: child?.name || "",
        key: child?.id || "",
        type: child?.children?.every((child) => child?.preview?.icon?.url)
          ? "img"
          : "text",
        showOutside: child?.priority <= 10,
        value: child?.filter_id || "",
        children: (
          child?.children ||
          child?.agg_filter_result_responses ||
          []
        ).map((child) => {
          if (child?.preview?.icon?.url) {
            return {
              icon: child.preview.icon.url || "",
              label: child?.name || "",
              value: child?.filter_id || "",
            };
          } else {
            return {
              label: child?.name || "",
              value: child?.filter_id || "",
            };
          }
        }),
      })) || [],
  };
};

function ActivityBanner() {
  const [activity, setActivity] = useState(null);
  const [date, setDate] = useLocalStorageState("hb-topic-activity-close-date", {
    defaultValue: null,
  });

  useReq(async () => {
    const result = await getDeliveryDetail("topic-fixed-banner");
    if (result) {
      setActivity(result?.pit_modules?.[0] || null);
    }
  });

  const onClose = useCallback(() => {
    setDate(dayjs().format("YYYY-MM-DD"));
  }, [setDate]);

  if (
    !activity ||
    !activity?.material?.activityShow ||
    date === dayjs().format("YYYY-MM-DD")
  ) {
    return null;
  }

  return (
    <div className={styles.activity}>
      <HBIcon className={styles.close} type="ic_clear2" onClick={onClose} />
      <a
        className={styles.container}
        href={activity?.material?.activityLink}
        target="_blank"
        rel="noreferrer"
        data-gd-click="resource_loc_click"
        data-gd-expose="resource_loc_expose"
        data-resource-type="banner"
        data-resource-id={activity?.module_id}
        data-plan-id={activity?.plan_id}
        data-resource-title={activity?.module_name}
        onClick={onClose}
      >
        <img
          className={styles.img}
          src={activity?.material?.activityImg}
          alt="活动图"
        />
        <When condition={activity?.material?.activityEndDate}>
          <Countdown
            className={styles.time}
            value={+(activity?.material?.activityEndDate * 1000)}
            format="D天HH:mm:ss"
          />
        </When>
      </a>
    </div>
  );
}

export async function getServerSideProps({ query, req, res }) {
  const { urlname: params, filter_id: urlFilterId } = query;

  if (urlFilterId && isNaN(+urlFilterId)) {
    return serverSideNotFound(res);
  }

  if (urlFilterId) {
    return serverSideRedirect(res, {
      destination: `/topics/${encodeURIComponent(params)}-${encodeURIComponent(
        urlFilterId
      )}`,
      permanent: true,
    });
  }

  const { urlname, filter_id } = splitTopicUrlnameAndFilterId(params);

  // 没有topic前缀的，重定向到有前缀的，保证链接统一（SEO优化需要)
  if (!(urlname as string).startsWith("topic-")) {
    return serverSideRedirect(res, {
      destination:
        `/topics/topic-${encodeURIComponent(urlname)}` +
        (filter_id ? "-" + filter_id : ""),
      permanent: true,
    });
  }

  const result = await getTopicDetail({ urlname });

  if (result.err || result instanceof Error) {
    if (result.err !== "404") {
      logger.error(result.msg, result);
    }
    return serverSideNotFound(res);
  }

  const topic = result.err ? null : filterData(result);

  // 如果filter_id不在topic的contents中，重定向到第一个,既没有filter_id
  if (filter_id) {
    const filterId = Number(filter_id);
    const isExist = topic?.contents?.some(
      (item) => item.filter.value === filterId
    );
    if (!isExist) {
      return serverSideRedirect(res, {
        destination: `/topics/${encodeURIComponent(urlname)}`,
        permanent: true,
      });
    }
  }

  const ids = filter_id || topic?.contents?.[0]?.filter?.value;

  let pins = [];
  let pinCount = null;
  let serverSideSimilarTopics = [];

  if (ids) {
    const filterName = topic.contents.find(
      (item) => item.filter.value === ids
    )?.name;

    const [similarTopics, topicPins] = await Promise.all([
      searchTopic({
        q: `${topic?.title} ${filterName}`,
        per_page: 25,
        page: 1,
        exclude_topic: isString(urlname)
          ? urlname.replace(/^topic-/, "")
          : urlname,
        dedupe: 1,
      }),
      getTopicPins({
        per_page: 80,
        page: 1,
        filter_ids: ids,
        sort: "all",
        position: "topics",
      }),
    ]);

    serverSideSimilarTopics = topicDetailFilter({
      topics: similarTopics.result || [],
    });

    pins = topicPins?.pins || [];
    pinCount = topicPins?.pin_count || null;
  }

  return {
    props: {
      serverSideTopic: topic,
      serverSidePins: pins,
      serverSidePinCount: pinCount,
      serverSideSimilarTopics,
    },
  };
}

export default function TopicPage({
  serverSideTopic,
  serverSidePins,
  serverSidePinCount,
  serverSideSimilarTopics,
}) {
  const router = useRouter();
  const { urlname: params } = router.query;
  const { urlname, filter_id } = splitTopicUrlnameAndFilterId(params);
  const [topic, setTopic] = useState(serverSideTopic);
  const user = useAppStore((s) => s.user);

  const [values, setValues] = useState(null);

  const filterInfo = useMemo(() => {
    return (
      topic?.contents?.find(
        (content) => content?.filter?.value + "" === filter_id
      ) || topic?.contents?.[0]
    );
  }, [filter_id, topic]);

  useReq(
    async () => {
      if (serverSideTopic && !user) {
        // 有服务端渲染数据，且用户未登录，则不请求数据
        return;
      }

      const result = await getTopicDetail({ urlname });
      if (result.err === 404) {
        router.push("/404", `/topics/${urlname}`);
      } else if (!result.err) {
        const data = filterData(result);
        setTopic(data);
      }
    },
    {
      refreshDeps: [urlname, user?.user_id],
    }
  );

  const onChange = useCallback((values) => {
    setValues((prev) => {
      if (isEqual(values, prev)) {
        return prev;
      }
      return values;
    });
  }, []);

  const keyword = useMemo(() => {
    if (!topic || !values) {
      return "";
    }
    return `${topic?.title} ${values?.filterOne}`;
  }, [topic, values]);

  const word = useMemo(() => {
    if (filter_id) {
      return (
        topic?.contents?.find(
          (content) => content?.filter?.value + "" === filter_id
        )?.title || ""
      );
    } else {
      return topic?.title || "";
    }
  }, [filter_id, topic]);

  const pageTitle = `${word}图片素材-高清${word}图片大全-花瓣网素材库`;
  const pageDescription = `花瓣网${word}专题为你提供${serverSidePinCount}个优质的${word}图片、${word}素材、高清图片、模板素材等，支持源下载。同时，还提供${
    topic?.labels?.length
      ? topic.labels
          .slice(0, 3)
          .map((label) => label.text)
          .join("、")
      : "其他素材"
  }等主题内容，助你快速完成设计。`;

  return (
    <div className={classNames(styles.page, "calculateWrapper")}>
      <Head>
        <title>{pageTitle}</title>
        <meta
          name="keywords"
          content={`${word}素材，${word}高清图片，${word}素材下载`}
        />
        <meta name="description" content={pageDescription} />
      </Head>
      <JsonLd
        data={{
          title: pageTitle,
          description: pageDescription,
          images:
            topic?.contents
              ?.slice(0, 3)
              ?.map((content) => imageUrl(content.img, { width: 240 })) || [],
          "@id": `https://huaban.com/topics/${urlname}`,
          pubDate: filterInfo?.created_at || null,
          upDate: filterInfo?.updated_at || null,
        }}
      />
      <div className={styles.banner}>
        <TopicBanner
          topic={topic}
          pinCount={serverSidePinCount}
          urlname={urlname}
        />
      </div>
      <div className={styles.filter}>
        <TopicFilter topic={topic} onChange={onChange} />
      </div>
      <div className={styles.pins}>
        <TopicLimitPinList
          values={values}
          defaultPins={serverSidePins}
          showDownload={true}
        />
      </div>
      <div className={styles.similar}>
        <TopicSimilarList
          keyword={keyword}
          serverSideSimilarTopics={serverSideSimilarTopics}
        />
      </div>
      <ActivityBanner />
    </div>
  );
}
